/**
 * Zustände anzeigen — und zwar **nie über Farbe allein**.
 *
 * `07` §7.14 verlangt „keine farbabhängige Statusinformation". Das ist keine
 * Formalie: Rot-Grün-Sehschwäche betrifft rund acht Prozent der Männer, und
 * die Monteur-App wird zusätzlich auf einem Display in der Sonne bedient, wo
 * Farbunterschiede verschwinden, bevor Formunterschiede es tun.
 *
 * Token allein können das nicht durchsetzen — sie liefern Farben. Erzwungen
 * wird es hier, im Bauteil: **Jeder Zustand trägt einen Text.** Es gibt keinen
 * Weg, dieses Bauteil ohne Beschriftung zu benutzen, weil `art` und `text`
 * beide Pflicht sind.
 *
 * Zusätzlich unterscheidet sich jeder Zustand in der Form — durch ein
 * vorangestelltes Zeichen. Wer die Farbe nicht sieht, sieht immer noch, dass
 * hier etwas anders ist als in der Zeile darüber.
 */

import type { ReactNode } from "react";

export const ZUSTANDSARTEN = ["erfolg", "warnung", "fehler", "neutral"] as const;
export type Zustandsart = (typeof ZUSTANDSARTEN)[number];

/**
 * Das Zeichen trägt dieselbe Aussage wie die Farbe.
 *
 * Bewusst Textzeichen und kein Symbolzeichensatz: Ein Zeichen aus der Schrift
 * skaliert mit ihr, wird von Vorlesewerkzeugen als Text behandelt und braucht
 * keine zweite Ladequelle. Ein Symbolzeichensatz, der nicht lädt, hinterlässt
 * ein leeres Kästchen — und dann ist die Aussage doch wieder nur die Farbe.
 */
const ZEICHEN: Record<Zustandsart, string> = {
  erfolg: "✓",
  warnung: "!",
  fehler: "×",
  neutral: "•",
};

export interface ZustandEigenschaften {
  readonly art: Zustandsart;
  /**
   * Was der Zustand bedeutet. **Pflicht** — ein Zustand ohne Text wäre genau
   * die farbabhängige Information, die `07` §7.14 ausschließt.
   */
  readonly text: string;
  /** Zusätzliche Erläuterung, etwa der Grund einer Warnung. */
  readonly kinder?: ReactNode;
}

export function Zustand({ art, text, kinder }: ZustandEigenschaften) {
  return (
    <span className={`zustand zustand--${art}`}>
      {/* `aria-hidden`: Der Text daneben sagt dasselbe, und ein vorgelesenes
          „Häkchen" vor jedem Eintrag wäre Lärm statt Information. */}
      <span className="zustand__zeichen" aria-hidden="true">{ZEICHEN[art]}</span>
      <span className="zustand__text">{text}</span>
      {kinder ? <span className="zustand__mehr">{kinder}</span> : null}
    </span>
  );
}

/** Für Prüfrezepte und das Handbuch: welches Zeichen zu welcher Art gehört. */
export function zeichenFuer(art: Zustandsart): string {
  return ZEICHEN[art];
}
